import type { V5Data, V5Frame } from '../../data/types'
import { fmtTemp } from '../../../shared/units'

export interface FusionReading { temperature: string; stamp: string; agreement: string; range: string; explanation: string; time: string }
const finite = (v: number | null | undefined): v is number => v != null && Number.isFinite(v)
const prepared = new WeakMap<readonly V5Frame[], Map<V5Frame, FusionReading>>()

export function prepareFusion(data: V5Data): Map<V5Frame, FusionReading> {
  const hit = prepared.get(data.frames)
  if (hit) return hit
  const result = new Map<V5Frame, FusionReading>()
  const count = data.report?.fusion.members.filter((m) => m.available && finite(m.weight) && m.weight > 0).length ?? 0
  const reading = (frame: V5Frame, now: boolean): FusionReading => {
    const hour = now ? null : data.models?.hours[frame.index]?.hour
    const confidence = now ? data.report?.fusion.confidence : hour?.confidence
    const range = hour?.temperature_range
    const agreement = count < 2 ? count === 1 ? 'One contributor cannot establish agreement.' : 'Model agreement is unavailable.' : !finite(confidence) ? 'Agreement not supplied for this time.' : confidence >= 0.8 ? 'Models cluster closely.' : confidence >= 0.5 ? 'Models have different outlooks.' : 'Models disagree widely.'
    const rangeText = range && finite(range.low) && finite(range.high) && range.low <= range.high ? `${fmtTemp(range.low, data.units.temp)}–${fmtTemp(range.high, data.units.temp)} supplied temperature range.` : 'Temperature range not supplied for this time.'
    return {
      temperature: frame.temperature, stamp: now ? 'Now' : frame.stamp, agreement,
      range: rangeText, explanation: `${agreement} ${rangeText} Agreement is not accuracy.`,
      time: String(frame.time),
    }
  }
  if (data.now) result.set(data.now, reading(data.now, true))
  for (const frame of data.frames) result.set(frame, reading(frame, false))
  if (data.report) prepared.set(data.frames, result)
  return result
}

export function sourceSummary(data: V5Data): string {
  if (!data.report) return data.pending ? 'Waiting for source status.' : 'Source status unavailable.'
  const sources = data.report.sources
  if (!sources.length) return 'Source availability not supplied.'
  const unavailable = sources.filter((s) => s.status === 'unavailable')
  const partial = sources.filter((s) => s.status === 'partial')
  const skipped = sources.filter((s) => s.status === 'skipped')
  return [
    `${sources.filter((s) => s.status === 'ok').length}/${sources.length} sources ready at report time`,
    unavailable.length ? `Unavailable: ${unavailable.map((s) => s.name).join(', ')}` : '',
    partial.length ? `Partial: ${partial.map((s) => s.name).join(', ')}` : '',
    skipped.length ? `Skipped: ${skipped.map((s) => s.name).join(', ')}` : '',
  ].filter(Boolean).join(' · ') + '.'
}
